import { STATUS_API } from 'src/config';

/**
 * Username availability check against GET /v1/group-user/is-available.
 *
 * The endpoint checks both the group-user table and the master-user table
 * (GroupUserService.isGroupLoginAvailable), so logins are globally unique
 * across both. It is unauthenticated but throttled to 15 requests per 10
 * minutes, so callers must only fire on blur — never per keystroke — and
 * results are cached per username for the lifetime of the page.
 *
 * Uses plain fetch rather than EmfitAPI: the endpoint needs no token, and
 * EmfitAPI's 401 handler wipes storage and redirects, which must never happen
 * because of a background availability probe.
 */
const cache = new Map<string, boolean>();

export type LoginAvailability = {
  /** False only when the backend positively said the name is taken. */
  available: boolean;
  /** True when the check could not be completed (offline, throttled, 5xx). */
  indeterminate: boolean;
};

export async function checkLoginAvailable(
  login: string
): Promise<LoginAvailability> {
  const name = login.trim();
  if (!name) {
    return { available: false, indeterminate: false };
  }

  const cached = cache.get(name);
  if (cached !== undefined) {
    return { available: cached, indeterminate: false };
  }

  try {
    const response = await fetch(
      `${STATUS_API}/v1/group-user/is-available?name=${encodeURIComponent(name)}`,
      { headers: { Accept: 'application/json' } }
    );

    if (!response.ok) {
      // 429 from the throttler, or a transient 5xx. Fall open so a legitimate
      // signup is never blocked by a failed probe - the backend is the final
      // authority on uniqueness anyway.
      return { available: true, indeterminate: true };
    }

    const body = (await response.json()) as { success?: boolean };
    const available = !!body?.success;
    cache.set(name, available);
    return { available, indeterminate: false };
  } catch {
    return { available: true, indeterminate: true };
  }
}

/** Drop a cached result, e.g. after a create attempt failed on uniqueness. */
export function forgetLoginAvailability(login: string): void {
  cache.delete(login.trim());
}
